<template>
  <div class="experience-section" ref="sectionRef">
    <!-- 导航标签 -->
    <div
      class="nav-tabs"
      :class="{ 'animate-in': isVisible }"
      style="--animation-order: 1"
    >
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :class="['tab-item', { active: activeTab === index }]"
        @click="activeTab = index"
      >
        {{ tab.title }}
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area">
      <div class="content-container">
        <div
          class="image-section"
          :class="{ 'animate-in': isVisible }"
          style="--animation-order: 2"
        >
          <img :src="tabs[activeTab].image" alt="Experience image" />
        </div>
        <div
          class="text-section"
          :class="{ 'animate-in': isVisible }"
          style="--animation-order: 3"
        >
          <h2 class="content-title">{{ tabs[activeTab].contentTitle }}</h2>
          <p class="content-description">{{ tabs[activeTab].description }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

// 导入图片
import img1 from "@/assets/imgsnew/tow/4-1.svg";
import img2 from "@/assets/imgsnew/tow/4-2.svg";
import img3 from "@/assets/imgsnew/tow/4-3.svg";

const sectionRef = ref(null);
const isVisible = ref(false);
const activeTab = ref(0);
let observer = null;

const observerOptions = {
  threshold: 0.2,
  rootMargin: "0px",
};

const handleIntersect = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      isVisible.value = true;
    } else {
      isVisible.value = false;
    }
  });
};

onMounted(() => {
  observer = new IntersectionObserver(handleIntersect, observerOptions);
  if (sectionRef.value) {
    observer.observe(sectionRef.value);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const tabs = [
  {
    title: "GOURMET FOOD",
    image: img1,
    contentTitle: "EXCLUSIVE THEATRE EXPERIENCE AT BBAR",
    description:
      "Explore the vibrant cocktail bars and restaurants at the heart of Victoria, just a stone's throw from London's iconic theatres. With live music, a signature menu with a contemporary twist, and resident mixologists crafting martinis, margaritas, and more, bbar offers cardholders a modern pre-theatre experience along with exclusive perks.",
  },
  {
    title: "JEWELRY",
    image: img2,
    contentTitle: "EXPLORE JEWELRY STORES",
    description:
      "Follow the manager on an exclusive guided tour of the jewelry stores in Place Vendôme. You will be immersed in classic Parisian haute couture and elegant style as you explore the boutiques of Boucheron, Van Cleef & Arpels, or Louis Vuitton*. Here, you can visit and listen to the fascinating stories of the history of high jewelry.",
  },
  {
    title: "WATCH",
    image: img3,
    contentTitle: "MAKING EXPERIENCE",
    description:
      "Unleash your inner craftsmanship at a leather-making masterclass guided by professional artisans at Atelier La Patine in Bordeaux. You will choose from three designs and create your own handbag using high-quality leather scraps. Learn valuable knowledge while assembling a bucket bag, a seasonal bag, or an envelope bag.",
  },
];
</script>

<style scoped lang="scss">
.experience-section {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 60px 80px;

  @media screen and (max-width: 768px) {
    padding: 30px 20px;
  }
}

.nav-tabs {
  display: flex;
  gap: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid #e5e7eb;
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: calc(var(--animation-order) * 0.1s);

  @media screen and (max-width: 768px) {
    gap: 20px;
    margin-bottom: 24px;
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &.animate-in {
    opacity: 1;
    transform: translateY(0);
  }

  .tab-item {
    padding: 12px 0;
    font-size: 16px;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;

    @media screen and (max-width: 768px) {
      font-size: 14px;
      padding: 8px 0;
    }

    &.active {
      color: #1d4ed8;

      &:after {
        content: "";
        position: absolute;
        bottom: -1px;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: #1d4ed8;
      }
    }

    &:hover {
      color: #1d4ed8;
    }
  }
}

.content-area {
  width: 100%;

  .content-container {
    display: flex;
    gap: 60px;
    align-items: center;

    @media screen and (max-width: 768px) {
      flex-direction: column;
      gap: 24px;
    }
  }

  .image-section {
    flex: 1;
    overflow: hidden;
    opacity: 0;
    transform: translateY(50px);
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    transition-delay: calc(var(--animation-order) * 0.1s);

    @media screen and (max-width: 768px) {
      width: 100%;
    }

    &.animate-in {
      opacity: 1;
      transform: translateY(0);
    }

    img {
      width: 100%;
      height: auto;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.1);
      }
    }
  }

  .text-section {
    flex: 1;
    opacity: 0;
    transform: translateY(50px);
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    transition-delay: calc(var(--animation-order) * 0.1s);

    @media screen and (max-width: 768px) {
      width: 100%;
    }

    &.animate-in {
      opacity: 1;
      transform: translateY(0);
    }

    .content-title {
      font-size: 24px;
      font-weight: 600;
      color: #111827;
      margin-bottom: 24px;

      @media screen and (max-width: 768px) {
        font-size: 20px;
        margin-bottom: 16px;
      }
    }

    .content-description {
      font-size: 16px;
      line-height: 1.6;
      color: #4b5563;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }
  }
}
</style>
